import WebApp from '@twa-dev/sdk';
import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import BottomNav from '@/components/BottomNav';
import { ensureSession } from '@/lib/auth';
import AnalyticsScreen from '@/routes/AnalyticsScreen';
import CandidateScreen from '@/routes/CandidateScreen';
import ColdSearchScreen from '@/routes/ColdSearchScreen';
import CriteriaScreen from '@/routes/CriteriaScreen';
import ShortlistScreen from '@/routes/ShortlistScreen';
import VacancyCandidatesScreen from '@/routes/VacancyCandidatesScreen';

/** Telegram passes the deeplink payload (``?startapp=...``) as
 * ``start_param``; when the page is opened with a raw query string (not via
 * the SDK) fall back to that. Notifications link candidates as
 * ``candidate_<uuid>`` and finished cold searches as
 * ``coldsearch_<vacancy_uuid>``. */
function readStartParam(): string | null {
  const fromSdk = WebApp.initDataUnsafe?.start_param;
  if (fromSdk) return fromSdk;
  const q = new URLSearchParams(window.location.search);
  return q.get('startapp') || q.get('tgWebAppStartParam');
}

// Our ids are UUIDs. Anything else is not a link we built, and a path the
// router would re-encode could never match the target — the app would sit
// on «Завантаження…» for good.
const ID = /^[0-9A-Za-z-]+$/;

interface DeeplinkTarget {
  pathname: string;
  search: string;
}

function deeplinkTarget(param: string | null): DeeplinkTarget | null {
  if (param?.startsWith('candidate_')) {
    const id = param.slice('candidate_'.length);
    if (!ID.test(id)) return null;
    // The card is about one vacancy; without it the candidate screen falls
    // back to the person's first application — another vacancy for a
    // re-suggestion.
    const vacancy = new URLSearchParams(window.location.search).get('vacancy');
    return {
      pathname: `/candidate/${id}`,
      search: vacancy && ID.test(vacancy) ? `?vacancy=${vacancy}` : '',
    };
  }
  if (param?.startsWith('coldsearch_')) {
    const id = param.slice('coldsearch_'.length);
    if (!ID.test(id)) return null;
    // "Search finished" notification → the cold-search screen resumes
    // the recruiter's latest search for this vacancy by itself.
    return { pathname: `/vacancy/${id}/cold-search`, search: '' };
  }
  return null;
}

/** Call a Telegram WebApp method that may be unsupported on older clients
 * without letting ``WebAppMethodUnsupported`` crash startup. */
function safeTelegram(fn: () => void): void {
  try {
    fn();
  } catch {
    /* method not supported by this client version — ignore */
  }
}

export default function App(): JSX.Element {
  const [authState, setAuthState] = useState<'pending' | 'ready' | { error: string }>('pending');
  const location = useLocation();
  const navigate = useNavigate();
  // Read before anything can navigate. The web_app buttons on our cards carry
  // the param in the query string only (Telegram fills ``start_param`` just
  // for t.me direct links), and the "/" → /shortlist redirect replaces the
  // URL: read after the routes mount, every card opened the shortlist
  // (24.09 meeting — the same list "from every vacancy").
  const [startTarget] = useState(() => deeplinkTarget(readStartParam()));
  const [deeplinkPending, setDeeplinkPending] = useState(startTarget !== null);

  useEffect(() => {
    safeTelegram(() => WebApp.ready());
    safeTelegram(() => WebApp.expand());
    ensureSession()
      .then(() => setAuthState('ready'))
      .catch((err: Error) => setAuthState({ error: err.message }));
  }, []);

  // Honour a deeplink once we're authenticated. The routes stay unmounted
  // until the location IS the target: react-router applies a navigation in
  // a transition, so a flag flipped next to navigate() would mount them on
  // the old "/" and let its redirect win again.
  useEffect(() => {
    if (authState !== 'ready' || !deeplinkPending || startTarget === null) return;
    if (location.pathname === startTarget.pathname) {
      setDeeplinkPending(false);
    } else {
      navigate(startTarget, { replace: true });
    }
  }, [authState, deeplinkPending, location.pathname, navigate, startTarget]);

  if (authState === 'pending' || (authState === 'ready' && deeplinkPending)) {
    return (
      <div className="flex h-full items-center justify-center text-tg-hint">
        Завантаження…
      </div>
    );
  }
  if (typeof authState === 'object') {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-tg-text">
        <h1 className="text-lg font-semibold">Не вдалося увійти</h1>
        <p className="text-sm text-tg-hint">{authState.error}</p>
        <p className="text-xs text-tg-hint">
          Натисніть /start у боті, щоб зареєструватися, і зверніться до адміністратора по доступ.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-tg-bg text-tg-text">
      <main className="flex-1 overflow-y-auto pb-16">
        <Routes>
          <Route path="/" element={<Navigate to="/shortlist" replace />} />
          <Route path="/shortlist" element={<ShortlistScreen />} />
          <Route path="/candidate/:candidateId" element={<CandidateScreen />} />
          <Route path="/vacancy/:vacancyId/candidates" element={<VacancyCandidatesScreen />} />
          <Route path="/vacancy/:vacancyId/criteria" element={<CriteriaScreen />} />
          <Route path="/vacancy/:vacancyId/cold-search" element={<ColdSearchScreen />} />
          <Route path="/analytics" element={<AnalyticsScreen />} />
          <Route path="*" element={<Navigate to="/shortlist" replace />} />
        </Routes>
      </main>
      <BottomNav currentPath={location.pathname} />
    </div>
  );
}
